'use client';

import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import { BREAKPOINTS } from '@/constants';
import { range } from '@/utils';

import { DemoSliderControl } from '@/components/Demo';
import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';

import GridDemo from './GridDemo';
import Item from './Item';
import {
  GridStructure as BaseGridStructure,
  BaseCodeWrapper,
} from './Grid.shared';

function FullBleed() {
  return (
    <GridDemo
      hidePerspectiveControl
      defaultPerspectiveEnabled={false}
      initialValues={{
        numOfCols: 3,
      }}
      customGrid={{
        gridTemplateColumns: '1fr',
      }}
      controls={[
        <SliderWrapper key="numOfCols">
          <DemoSliderControl
            id="numOfCols"
            label="Number of Columns"
            min={1}
            max={6}
            step={1}
            handleFocusColor="white"
            wrapperStyle={{
              width: 400,
              maxWidth: '60vw',
            }}
          />
        </SliderWrapper>,
      ]}
    >
      {({ numOfCols, isPerspectiveEnabled }) => {
        const cssCode = `\
.parent {
  display: grid;
  grid-template-columns:
    repeat(${numOfCols}, 1fr);
}

.child {
  grid-column: 1 / -1;
  grid-row: 2;
}`;

        return (
          <OuterWrapper>
            <Row>
              <CodeWrapper>
                <ClientOnlySnippet
                  omitCopyButton
                  lockedToColorMode="dark"
                  code={cssCode}
                  lang="css"
                />
              </CodeWrapper>
              <DemoWrapper>
                <Layer
                  style={{
                    transform: isPerspectiveEnabled
                      ? 'translateX(10%) skewY(18deg) scaleX(0.85)'
                      : 'translateX(0%) skewY(0deg) scaleX(1)',
                  }}
                >
                  <GridStructure
                    style={{
                      gridTemplateColumns: `repeat(${numOfCols}, 1fr)`,
                    }}
                  >
                    {range(1, numOfCols + 1).map((colNum) => (
                      <GridColumn
                        key={colNum}
                        style={{
                          gridColumn: colNum,
                          borderRight:
                            colNum === numOfCols ? 'none' : undefined,
                        }}
                      />
                    ))}
                    {range(1, 4).map((rowNum) => (
                      <GridRow
                        key={rowNum}
                        style={{ gridRow: rowNum }}
                      />
                    ))}
                  </GridStructure>
                </Layer>
                <FrontLayer
                  style={{
                    transform: isPerspectiveEnabled
                      ? 'translateX(-10%) skewY(18deg) scaleX(0.85)'
                      : 'translateX(0%) skewY(0deg) scaleX(1)',
                  }}
                >
                  <ChildWrapper
                    style={{
                      gridTemplateRows: 'repeat(3, 1fr)',
                      gridTemplateColumns: `repeat(${numOfCols}, 1fr)`,
                    }}
                  >
                    <Item
                      layout={false}
                      style={{
                        opacity: 0.8,
                        gridRow: 2,
                        gridColumn: '1 / -1',
                        marginLeft: 0,
                        marginRight: 0,
                      }}
                    />
                  </ChildWrapper>
                </FrontLayer>
              </DemoWrapper>
            </Row>
          </OuterWrapper>
        );
      }}
    </GridDemo>
  );
}

const SliderWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const OuterWrapper = styled.div`
  margin: 32px 0px 16px;
  width: 100%;
  isolation: isolate;

  @media (max-width: 46.875rem) {
    margin: 48px 0px 16px;
  }

  @media (max-width: 30rem) {
    margin-bottom: -8px;
    margin-top: 8px;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  height: 20.5rem;

  @media (max-width: 750px) {
    flex-direction: column-reverse;
    height: unset;
  }

  @media ${BREAKPOINTS.smAndSmaller} {
    margin-inline: -8px;
    width: calc(100% + 16px);
  }
`;

const DemoWrapper = styled.div`
  position: relative;
  width: 250px;

  @media (max-width: 750px) {
    width: 100%;
    max-width: 358px;
    height: 11.25rem;
    margin-inline: auto;
  }
`;

const Layer = styled(motion.div)`
  transition: transform 500ms;
  will-change: transform;
  height: 100%;
`;

const FrontLayer = styled(Layer)`
  position: absolute;
  inset: 0;
  width: 100%;
`;

const ChildWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  gap: 2px;
  padding: 2px;
`;

const GridStructure = styled(BaseGridStructure)`
  grid-template-columns: 1fr 3fr;
  height: 100%;
`;

const GridRow = styled.div`
  grid-column: 1 / -1;
  border: 1px solid transparent;
  transform: translateY(0.5px);

  &:not(:last-of-type) {
    border-bottom: 2px dashed var(--color-gray-500);
  }
`;

const GridColumn = styled.div`
  grid-row: 1 / 4;
  grid-column: 1;
  border: 1px solid transparent;
  border-right: 2px dashed var(--color-gray-500);
  transform: translateX(0.5px);
`;

const CodeWrapper = styled(BaseCodeWrapper)`
  position: relative;
  z-index: 2;
  flex: unset;
  width: 360px;

  && {
    background: hsl(210deg 15% 6% / 0.33);
  }

  @media (max-width: 30rem) {
    width: calc(100% + 16px);
    max-width: calc(100% + 16px);
    margin-inline: -8px;
    padding-inline: 3px;
    border-radius: 0px 0px 8px 8px;
  }
`;

export default FullBleed;
